<template>
  <div class="panel-section p-bottom-8">
    <div
      class="panel-item is-flex is-align-items-center is-justify-content-space-between"
      :class="{ 'panel-item--editable': $can('publish', 'Request') }"
    >
      <p class="has-text-weight-semibold">Received</p>
      <NrTooltip
        label="Edit request received date"
        v-if="$can('publish', 'Request')"
      >
        <NrIconButton
          variant="minimal-neutral"
          :icon="{ name: 'pencil-alt', type: 'regular' }"
          aria-label="Edit request received date"
          @click="setCurrentAction('received_date')"
        />
      </NrTooltip>
    </div>
    <div class="m-left-8">
      <p>{{ formattedReceivedDate }}</p>
    </div>
    <NrModal
      title="Change received date"
      :show="currentAction === 'received_date'"
      :hasSecondaryButton="true"
      secondaryButtonText="Cancel"
      primaryButtonText="Change date"
      @on-primary-click="submitReceivedDateChange"
      @on-secondary-click="setCurrentAction(null)"
      @on-close-click="setCurrentAction(null)"
    >
      <form @submit.prevent.stop>
        <fieldset>
          <NrDatePicker
            @input="handleReceivedDateChange"
            placeholder="Type or select a date (mm/dd/yyyy)"
            :modelValue="computedReceivedDate"
            :unselectable-dates="unselectableReceivedDate"
          >
            <template v-slot:label>New received date</template>
            <template v-slot:trigger><NrIcon name="calendar-alt" /></template>
          </NrDatePicker>
        </fieldset>
        <fieldset>
          <div class="field m-top-8">
            <NrFormInput
              id="received-date-comment"
              label="Comments (optional)"
              rows="8"
              placeholder="Enter an optional reason for changing the received date. This message posts to timeline."
              tag="textarea"
              v-model="receivedDateComment"
            />
            <p class="help">
              Changing this date may have legal consequences. Please refer to
              appropriate laws.
            </p>
          </div>
        </fieldset>
      </form>
    </NrModal>
  </div>
</template>

<script lang="ts">
import { format } from 'date-fns'
import { defineComponent } from 'vue'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrDatePicker from '@nextrequestco/components/src/components/NrDatePicker'
import NrSentry from '../../../utils/nr_sentry'
import { mapState, mapActions } from 'vuex'

export default defineComponent({
  name: 'request-dates',
  components: {
    NrModal,
    NrIconButton,
    NrFormInput,
    NrIcon,
    NrTooltip,
    NrDatePicker
  },
  props: {
    prettyId: {
      type: String,
      required: true
    },
    date: {
      type: String
    },
    requestSubmitType: {
      type: String
    }
  },
  data() {
    return {
      receivedDateComment: '',
      selectedReceivedDate: null
    }
  },
  computed: {
    ...mapState('request_show', ['currentAction']),
    computedReceivedDate(): String | null {
      const date = format(new Date(this.date), 'MM/dd/yyyy')
      if (date) {
        return date
      }
      return null
    },
    unselectableReceivedDate(): Date[] {
      if (this.computedReceivedDate) {
        return [this.computedReceivedDate]
      } else {
        return []
      }
    },
    formattedReceivedDate(): string {
      const date = format(new Date(this.date), 'MMMM dd, yyyy')
      const submitTypeMap: { [key: string]: string } = {
        Fax: 'via fax',
        Email: 'via email',
        'In person': 'in person',
        Phone: 'via phone',
        Web: 'via web',
        Mail: 'via mail',
        Import: 'via request uploader'
      }

      const submitType = this.requestSubmitType
        ? submitTypeMap[this.requestSubmitType] || 'via web'
        : 'via web'

      return `${date} ${submitType}`
    }
  },
  methods: {
    ...mapActions('request_show', ['setCurrentAction', 'updateReceivedDate']),
    submitReceivedDateChange(): void {
      if (!this.selectedReceivedDate) {
        this.$toasted.error('You must select a received date')
        return
      }
      const params = {
        date: format(new Date(this.selectedReceivedDate), 'MM/dd/yyyy'),
        prettyId: this.prettyId,
        description: this.receivedDateComment
      }
      this.updateReceivedDate(params)
        .then(() => {
          this.setCurrentAction(null)
          this.$toasted.success('Successfully updated the received date')
        })
        .catch((error) => {
          this.$toasted.error(
            'There was an error updating the received date for the request. Make sure the date is not the current received date. Please reload the page and try again.'
          )
          NrSentry.captureError(error)
        })
    },
    handleReceivedDateChange(date): void {
      this.selectedReceivedDate = date
    }
  }
})
</script>
